import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, OverlayTrigger, Tooltip, ProgressBar, Modal,
    InputGroup, Form, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function SauvegardeRestauration() {
    const navigate = useNavigate();
    const [sauvegardes, setSauvegardes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [stats, setStats] = useState(null);
    
    // États pour la recherche
    const [searchText, setSearchText] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8);

    // États pour le modal de restauration
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [fileToRestore, setFileToRestore] = useState(null);

    useEffect(() => {
        fetchSauvegardes();
        fetchStats();
    }, []);

    const fetchSauvegardes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=sauvegardes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSauvegardes(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=stats_sauvegardes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const handleRestoreClick = (fichier) => {
        setFileToRestore(fichier);
        setShowRestoreModal(true);
    };

    const handleRestoreConfirm = async () => {
        if (!fileToRestore) return;
        
        setShowRestoreModal(false);
        setRestoring(true);
        setError(null);
        setSuccess('');
        
        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'restaurer_sauvegarde',
                        fichier: fileToRestore
                    })
                }
            );
            
            const text = await response.text();
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${text.substring(0, 200)}`);
            }
            
            if (!text || text.trim() === '') {
                throw new Error("La réponse du serveur est vide");
            }
            
            let result;
            try {
                result = JSON.parse(text);
            } catch (parseError) {
                console.error("Erreur de parsing JSON:", parseError);
                throw new Error("Le serveur n'a pas renvoyé un JSON valide");
            }
            
            if (result.success) {
                setSuccess("✅ Restauration réussie ! La page va se recharger...");
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                setError(result.message || "Erreur lors de la restauration");
            }
        } catch (err) {
            console.error("Erreur complète:", err);
            setError(err.message);
        } finally {
            setRestoring(false);
            setFileToRestore(null);
        }
    };

    const handleDownload = async (fichier) => {
        try {
            const token = localStorage.getItem("token");
            
            const downloadUrl = `${API_BASE_URL}/parametres.php?action=telecharger_sauvegarde&fichier=${encodeURIComponent(fichier)}&token=${token}`;
            
            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur lors du téléchargement");
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fichier;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            setSuccess(`📥 Téléchargement de ${fichier} démarré`);
            setTimeout(() => setSuccess(''), 3000);
            
        } catch (err) {
            setError("Erreur lors du téléchargement: " + err.message);
        }
    };

    const formatTaille = (octets) => {
        if (!octets) return '-';
        if (octets < 1024) return octets + ' o';
        if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
        return (octets / (1024 * 1024)).toFixed(1) + ' Mo';
    };

    const getNiveauRisque = (date) => {
        const jours = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
        if (jours <= 7) return { color: 'success', text: 'Récente', icon: 'ri-check-line' };
        if (jours <= 30) return { color: 'warning', text: 'Mois', icon: 'ri-time-line' };
        return { color: 'danger', text: 'Ancienne', icon: 'ri-alert-line' };
    };

    // Filtrage des sauvegardes
    const filteredSauvegardes = useMemo(() => {
        return sauvegardes.filter(s => 
            s.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
            formatDate(s.date).toLowerCase().includes(searchText.toLowerCase())
        );
    }, [sauvegardes, searchText]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSauvegardes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSauvegardes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                size="sm" 
                onClick={onClick} 
                className="action-btn"
                disabled={disabled}
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des sauvegardes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête modernisé */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-warning bg-opacity-10 rounded-3">
                                    <i className="ri-restart-line fs-20 text-warning"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Restauration</h4>
                                    <p className="text-muted small mb-0">Restaurer une sauvegarde précédente</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/parametres/sauvegarde/manuel')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-database-2-line"></i>
                                Sauvegarde
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-database-2-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total sauvegardes</span>
                                        <h3 className="kpi-value">{stats.total || sauvegardes.length}</h3>
                                        <span className="kpi-unit">fichiers</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-hard-drive-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Espace utilisé</span>
                                        <h3 className="kpi-value">{formatTaille(stats.espace_total || 0)}</h3>
                                        <span className="kpi-unit">stockage</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-calendar-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Dernière sauvegarde</span>
                                        <h3 className="kpi-value">{stats.derniere_sauvegarde ? formatDate(stats.derniere_sauvegarde) : '-'}</h3>
                                        <span className="kpi-unit">la plus récente</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-shield-check-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Niveau sécurité</span>
                                        <h3 className="kpi-value">{stats.niveau_securite || 'Bon'}</h3>
                                        <span className="kpi-unit">recommandations</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Avertissement */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 bg-warning bg-opacity-10">
                        <Card.Body className="p-3 p-md-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-warning bg-opacity-25 rounded-circle p-3 me-3 flex-shrink-0">
                                    <i className="ri-alert-line text-warning fs-4"></i>
                                </div>
                                <div>
                                    <h6 className="text-warning mb-1 fw-bold"> Attention - Opération irréversible</h6>
                                    <p className="mb-0 text-muted small">
                                        La restauration d'une sauvegarde va remplacer toutes les données actuelles par celles de la sauvegarde sélectionnée.
                                        Cette action est définitive et ne peut pas être annulée.
                                    </p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Progression si restauration en cours */}
                    {restoring && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="ri-restart-line me-2 text-warning animate-spin"></i>
                                        Restauration en cours...
                                    </h6>
                                    <Spinner size="sm" variant="warning" />
                                </div>
                                <ProgressBar 
                                    animated 
                                    now={100} 
                                    variant="warning" 
                                    style={{ height: '6px', borderRadius: '3px' }}
                                />
                                <p className="text-muted small mt-3 mb-0">
                                    <i className="ri-information-line me-1"></i>
                                    Veuillez patienter, la restauration peut prendre quelques instants.
                                </p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Messages */}
                    {success && (
                        <Alert variant="success" className="rounded-3 border-0 mb-4 shadow-sm">
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                                <div>
                                    <strong className="text-success">Succès</strong>
                                    <p className="mb-0 small">{success}</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" className="rounded-3 border-0 mb-4 shadow-sm">
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                                <div>
                                    <strong className="text-danger">Erreur</strong>
                                    <p className="mb-0 small">{error}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={8}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un fichier ou une date..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-file-list-line me-1"></i>
                                        {filteredSauvegardes.length} fichier(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des sauvegardes avec pagination */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Date</th>
                                            <th style={{minWidth:'300px'}}>Fichier</th>
                                            <th>Taille</th>
                                            <th>Âge</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((s, index) => {
                                                const risque = getNiveauRisque(s.date);
                                                return (
                                                    <tr key={index} className="backup-row">
                                                        <td className="ps-4">
                                                            <div className="d-flex flex-column">
                                                                <small className="fw-semibold">
                                                                    <i className="ri-calendar-line me-1 text-muted fs-10"></i>
                                                                    {formatDate(s.date)}
                                                                </small>
                                                                <small className="text-muted">
                                                                    <i className="ri-time-line me-1 fs-10"></i>
                                                                    {new Date(s.date).toLocaleTimeString('fr-FR')}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <code className="bg-light px-2 py-1 rounded" style={{ fontSize: '0.75rem' }}>
                                                                {s.nom}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary" className="rounded-pill px-3 py-1 fw-normal">
                                                                {formatTaille(s.taille)}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge status-${risque.color}`}>
                                                                <i className={`${risque.icon} me-1 fs-10`}></i>
                                                                {risque.text}
                                                            </span>
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    variant="outline-warning"
                                                                    icon="ri-restart-line"
                                                                    onClick={() => handleRestoreClick(s.nom)}
                                                                    tooltip="Restaurer cette sauvegarde"
                                                                    // disabled={restoring}
                                                                    disabled={true}
                                                                />
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-download-line"
                                                                    onClick={() => handleDownload(s.nom)}
                                                                    tooltip="Télécharger"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-inbox-line empty-icon text-warning" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune sauvegarde trouvée</h5>
                                                        <p className="text-muted mb-3 small">
                                                            {searchText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Créez d'abord une sauvegarde dans l'onglet 'Sauvegarde manuelle'"}
                                                        </p>
                                                        {searchText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setSearchText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/parametres/sauvegarde/manuel')}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-database-2-line me-2"></i>
                                                                Créer une sauvegarde
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredSauvegardes.length)} sur {filteredSauvegardes.length} sauvegardes
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Recommandations */}
                    {sauvegardes.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4 bg-light">
                            <Card.Body className="p-3 p-md-4">
                                <Row className="align-items-center">
                                    <Col md={8}>
                                        <div className="d-flex align-items-center text-muted small">
                                            <i className="ri-information-line me-2 fs-5 text-info"></i>
                                            <span>
                                                <strong>Recommandation :</strong> Pour une sécurité optimale, 
                                                conservez au moins une sauvegarde par semaine et testez régulièrement 
                                                la procédure de restauration.
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4} className="text-end mt-2 mt-md-0">
                                        <Badge bg="info" className="px-3 py-2 rounded-pill">
                                            <i className="ri-shield-check-line me-1"></i>
                                            Bonnes pratiques
                                        </Badge>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {/* Modal de confirmation de restauration modernisé */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered className="modern-modal restore-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#f59e0b' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-restart-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Restaurer la sauvegarde</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Vous êtes sur le point de restaurer la sauvegarde <br />
                            <strong className="text-warning">"{fileToRestore}"</strong>
                        </p>
                        
                        <Alert variant="danger" className="rounded-3 text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>⚠️ Cette action est irréversible !</strong>
                                    <p className="mb-0 small mt-1">
                                        La restauration va écraser TOUTES les données actuelles par celles de la sauvegarde.
                                        Assurez-vous d'avoir une sauvegarde récente de vos données actuelles.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Fichier à restaurer :</span>
                                <code className="bg-white p-2 rounded small">{fileToRestore}</code>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowRestoreModal(false)}
                            className="px-4 rounded-pill"
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={handleRestoreConfirm}
                            className="px-5 rounded-pill"
                            style={{ background: '#f59e0b', border: 'none' }}
                        >
                            <i className="ri-restart-line me-2"></i>
                            Restaurer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                .status-success { background: #d1fae5; color: #065f46; }
                .status-warning { background: #fed7aa; color: #92400e; }
                .status-danger { background: #fee2e2; color: #991b1b; }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .backup-row {
                    transition: all 0.2s ease;
                }
                
                .backup-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                /* Animations */
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                
                /* Utility Classes */
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                
                .empty-state {
                    text-align: center;
                    padding: 2rem;
                }
                .empty-icon {
                    opacity: 0.5;
                }
            `}</style>
        </>
    );
}